import React from 'react';
import { DatabaseBackup, RefreshCw, ShieldAlert } from 'lucide-react';
import { securityService } from '../../services/adminService';
import type { SecurityLogDetails, SecurityLogEntry, SecurityLogPage, SiemEvent, SiemPage, SiemSummary } from '../../services/adminService';
import DismissibleError from '../DismissibleError';
import { EmptyTableState, RecordsTableCard, StatusBadge } from '../records';

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'purple';

type LockoutEventName =
  | 'FAILED_LOGIN_ATTEMPT'
  | 'ACCOUNT_LOCKED'
  | 'LOCKED_ACCOUNT_ACCESS_ATTEMPT'
  | 'ACCOUNT_AUTOMATICALLY_UNLOCKED'
  | 'ACCOUNT_UNLOCKED_BY_ADMIN';

interface LockoutEventPresentation {
  label: string;
  severity: string;
  severityTone: BadgeTone;
  status: string;
  statusTone: BadgeTone;
  fallbackReason: string;
}

interface BackupStatus {
  last_backup_status?: string | null;
  status?: string | null;
}

interface BackupRun {
  id: number | string;
  started_at?: string;
  backup_type?: string;
  trigger_source?: string;
  status?: string;
  encrypted?: boolean;
}

const lockoutEventPresentation: Record<LockoutEventName, LockoutEventPresentation> = {
  FAILED_LOGIN_ATTEMPT: {
    label: 'Failed Login Attempt',
    severity: 'Warning',
    severityTone: 'warning',
    status: 'Failed',
    statusTone: 'danger',
    fallbackReason: 'Invalid credentials',
  },
  ACCOUNT_LOCKED: {
    label: 'Account Locked',
    severity: 'Critical',
    severityTone: 'danger',
    status: 'Locked',
    statusTone: 'danger',
    fallbackReason: 'Maximum failed attempts reached',
  },
  LOCKED_ACCOUNT_ACCESS_ATTEMPT: {
    label: 'Locked Account Access Attempt',
    severity: 'Warning',
    severityTone: 'warning',
    status: 'Blocked',
    statusTone: 'warning',
    fallbackReason: 'Account locked',
  },
  ACCOUNT_AUTOMATICALLY_UNLOCKED: {
    label: 'Account Automatically Unlocked',
    severity: 'Info',
    severityTone: 'info',
    status: 'Unlocked',
    statusTone: 'success',
    fallbackReason: 'Lockout expired',
  },
  ACCOUNT_UNLOCKED_BY_ADMIN: {
    label: 'Account Unlocked by Administrator',
    severity: 'Info',
    severityTone: 'info',
    status: 'Unlocked',
    statusTone: 'success',
    fallbackReason: 'Administrator action',
  },
};

const lockoutReasonLabels: Record<string, string> = {
  invalid_credentials: 'Invalid credentials',
  maximum_failed_attempts_reached: 'Maximum failed attempts reached',
  account_locked: 'Account locked',
  lockout_expired: 'Lockout expired',
  administrator_action: 'Administrator action',
};

const value = (input: unknown, fallback = '-'): string =>
  input === null || input === undefined || input === '' ? fallback : String(input);

const formatDateTime = (input?: string): string => {
  if (!input) return '-';
  const date = new Date(input);
  return Number.isNaN(date.getTime()) ? input : date.toLocaleString();
};

const normalizedEvent = (event: unknown): string => value(event, '').trim().toUpperCase();

const getLockoutPresentation = (event: unknown): LockoutEventPresentation | null =>
  lockoutEventPresentation[normalizedEvent(event) as LockoutEventName] || null;

const safeDetails = (log: SecurityLogEntry): SecurityLogDetails =>
  log.details && typeof log.details === 'object' && !Array.isArray(log.details) ? log.details : {};

const accountLabel = (log: SecurityLogEntry, details: SecurityLogDetails): string =>
  value(details.username || log.username || details.user_id || log.user, 'Unknown account');

const reasonLabel = (details: SecurityLogDetails, presentation: LockoutEventPresentation): string => {
  const reason = typeof details.reason === 'string' ? details.reason.toLowerCase() : '';
  return lockoutReasonLabels[reason] || presentation.fallbackReason;
};

const administratorUnlockLabel = (event: unknown, details: SecurityLogDetails): string => {
  if (normalizedEvent(event) !== 'ACCOUNT_UNLOCKED_BY_ADMIN') return '-';
  const administrator = value(details.unlocked_by_username || details.unlocked_by_id, 'Administrator');
  return `${administrator} (Administrative)`;
};

const eventCategories: Record<string, string> = {
  'auth.login_failed': 'Failed login',
  'auth.account_locked': 'Account locked',
  'auth.locked_access': 'Blocked sign-in to locked account',
  'auth.account_unlocked_automatic': 'Account automatically unlocked',
  'auth.account_unlocked_admin': 'Account unlocked by administrator',
  'access.denied': 'Access denied',
  'ssh.login_failed': 'SSH login failed',
  'ssh.invalid_user': 'SSH invalid-user attempt',
  'openappsec.detect': 'Web request detected',
  'openappsec.prevent': 'Web request blocked',
  'nginx.rate_limited': 'Request throttled (HTTP 429)',
  'fim.created': 'File created',
  'fim.modified': 'File changed',
  'fim.deleted': 'File deleted',
};

const eventSources: Record<string, string> = {
  application: 'Application', openappsec: 'Open-appsec', nginx: 'Nginx',
  ssh: 'SSH', fim: 'FIM canary', fim_live: 'Protected FIM',
};

const eventDetail = (event: SiemEvent): string => {
  const data: Record<string, unknown> = event.metadata || {};
  if (event.category.startsWith('fim.') && (event.source === 'fim' || event.source === 'fim_live')) {
    const path = data.path;
    return typeof path === 'string' && path.length <= 200 && !path.startsWith('/') &&
      !path.split('/').includes('..') ? `File: ${path}` : '-';
  }
  if (event.source === 'application') {
    const reason = typeof data.reason === 'string' ? lockoutReasonLabels[data.reason] : undefined;
    const count = data.failed_attempt_count;
    return [reason, typeof count === 'number' && Number.isInteger(count) && count >= 0 ? `Failed attempts: ${count}` : undefined]
      .filter(Boolean).join(' · ') || '-';
  }
  return '-';
};

const beginningOfDay = (day: string): string | undefined =>
  day ? new Date(`${day}T00:00:00`).toISOString() : undefined;

const endOfDayExclusive = (day: string): string | undefined => {
  if (!day) return undefined;
  const end = new Date(`${day}T00:00:00`);
  end.setDate(end.getDate() + 1);
  return end.toISOString();
};

const SecurityCenter: React.FC = () => {
  const [securityLogs, setSecurityLogs] = React.useState<SecurityLogEntry[]>([]);
  const [siemSummary, setSiemSummary] = React.useState<SiemSummary | null>(null);
  const [siemPage, setSiemPage] = React.useState<SiemPage | null>(null);
  const [eventPage, setEventPage] = React.useState(1);
  const [lockoutPage, setLockoutPage] = React.useState(1);
  const [lockoutData, setLockoutData] = React.useState<SecurityLogPage | null>(null);
  const [sourceFilter, setSourceFilter] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState('');
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [backupStatus, setBackupStatus] = React.useState<BackupStatus | null>(null);
  const [backupRuns, setBackupRuns] = React.useState<BackupRun[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [runningBackup, setRunningBackup] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');
  const latestRequest = React.useRef(0);

  const eventFilters = React.useMemo(() => ({
    source: sourceFilter || undefined,
    category: categoryFilter || undefined,
    from: beginningOfDay(startDate),
    before: endOfDayExclusive(endDate),
  }), [sourceFilter, categoryFilter, startDate, endDate]);

  const load = React.useCallback(async () => {
    const request = ++latestRequest.current;
    setError('');
    try {
      const [nextLogs, nextLockoutLogs, nextBackupStatus, nextBackupRuns, nextSummary, nextEvents] = await Promise.all([
        securityService.getSecurityLogs({ event: 'session_timeout' }),
        securityService.getSecurityLogPage({ event_type: 'lockout' }, lockoutPage),
        securityService.getBackupStatus(),
        securityService.getBackupRuns(),
        securityService.getSiemSummary(),
        securityService.getSiemEvents(eventPage, eventFilters),
      ]);
      if (request !== latestRequest.current) return;
      setSecurityLogs(nextLogs);
      setLockoutData(nextLockoutLogs);
      setBackupStatus(nextBackupStatus);
      setBackupRuns(nextBackupRuns);
      setSiemSummary(nextSummary);
      setSiemPage(nextEvents);
    } catch {
      if (request === latestRequest.current) setError('Security center data is temporarily unavailable.');
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  }, [eventPage, eventFilters, lockoutPage]);

  React.useEffect(() => { void load(); }, [load]);

  const runBackup = async () => {
    setRunningBackup(true);
    setError('');
    setMessage('');
    try {
      await securityService.runBackup();
      setMessage('Backup job started successfully.');
      await load();
    } catch {
      setError('Backup job could not be started.');
    } finally {
      setRunningBackup(false);
    }
  };

  const sessionEvents = securityLogs.filter((log) => normalizedEvent(log.event) === 'SESSION_TIMEOUT');
  const recentLockoutLogs = (lockoutData?.results || []).filter((log) => getLockoutPresentation(log.event));

  if (loading) return <div className="loading-state" role="status" aria-live="polite"><span className="loading-mark" />Loading security center...</div>;

  return (
    <div className="admin-dashboard">
      <div className="page-heading">
        <div><h1>Security Center</h1><p>Security events, authentication signals, sessions, and backup health.</p></div>
        <div className="table-actions">
          <button className="btn btn-primary" type="button" onClick={runBackup} disabled={runningBackup}>
            <DatabaseBackup size={14} aria-hidden="true" />{runningBackup ? 'Running...' : 'Run Backup'}
          </button>
          <button className="btn btn-secondary" type="button" onClick={load}><RefreshCw size={14} aria-hidden="true" />Refresh</button>
        </div>
      </div>

      {message && <div className="success-message" role="status" aria-live="polite">{message}</div>}
      {error && <DismissibleError message={error} onDismiss={() => setError('')} onRetry={load} />}

      <div className="stats-grid">
        <article className="stat-card stat-card--multiline-note"><span className="metric-icon neutral"><ShieldAlert size={20} aria-hidden="true" /></span><div><span className="stat-label">Security Events</span><strong className="stat-number">{siemSummary?.events ?? '—'}</strong><span className="stat-note" title="Accepted application and host events · last 24 hours">Accepted application and host events · last 24 hours</span></div></article>
        <article className="stat-card"><span className="metric-icon neutral"><ShieldAlert size={20} aria-hidden="true" /></span><div><span className="stat-label">Failed Login Attempts</span><strong className="stat-number">{siemSummary?.failed_logins ?? '—'}</strong><span className="stat-note">Deduplicated SIEM events · last 24 hours</span></div></article>
        <article className="stat-card"><span className="metric-icon purple"><ShieldAlert size={20} aria-hidden="true" /></span><div><span className="stat-label">Failed MFA Attempts</span><strong className="stat-number">{siemSummary?.failed_mfa_audit ?? '—'}</strong><span className="stat-note">Audit log · last 24 hours</span></div></article>
        <article className="stat-card"><span className="metric-icon blue"><DatabaseBackup size={20} aria-hidden="true" /></span><div><span className="stat-label">Backup Status</span><strong className="stat-number">{value(backupStatus?.last_backup_status || backupStatus?.status, 'None')}</strong><span className="stat-note">Latest backup</span></div></article>
      </div>

      <section className="panel" style={{ marginBottom: '24px' }}>
        <div className="section-heading"><h2>Security Event History</h2><span>{siemSummary?.alerts ?? 0} stored alerts</span></div>
        <p>Paginated accepted events; correlated alerts are counted separately. Failed MFA is counted from the audit log above.</p>
        <div className="table-actions">
          <label>Source <select value={sourceFilter} onChange={(e) => { setSourceFilter(e.target.value); setEventPage(1); }}>
            <option value="">All sources</option>
            {Object.entries(eventSources).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select></label>
          <label>Category <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setEventPage(1); }}>
            <option value="">All categories</option>
            {Object.entries(eventCategories).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select></label>
          <label>From <input type="date" value={startDate} onChange={(e) => {
            setStartDate(e.target.value);
            if (endDate && e.target.value > endDate) setEndDate('');
            setEventPage(1);
          }} /></label>
          <label>Through <input type="date" min={startDate || undefined} value={endDate} onChange={(e) => { setEndDate(e.target.value); setEventPage(1); }} /></label>
        </div>
        <div className="table-wrap"><table><thead><tr><th>Time</th><th>Source</th><th>Event</th><th>Details</th></tr></thead><tbody>
          {siemPage?.results.length ? siemPage.results.map((event) => (
            <tr key={event.id}><td>{formatDateTime(event.occurred_at)}</td>
              <td>{eventSources[event.source] || 'Other source'}</td>
              <td>{eventCategories[event.category] || 'Other security event'}</td>
              <td>{eventDetail(event)}</td></tr>
          )) : <tr><td colSpan={4}>No security events match these filters.</td></tr>}
        </tbody></table></div>
        <div className="table-actions">
          <button type="button" disabled={!siemPage?.previous} onClick={() => setEventPage((page) => Math.max(1, page - 1))}>Previous</button>
          <span>Page {eventPage} · {siemPage?.count ?? 0} events</span>
          <button type="button" disabled={!siemPage?.next} onClick={() => setEventPage((page) => page + 1)}>Next</button>
        </div>
      </section>

      <section className="panel" style={{ marginBottom: '24px' }}>
        <div className="section-heading"><h2>Collection Health</h2><span>Operational status</span></div>
        <p>Application outbox pending: {siemSummary?.pending_outbox ?? 'Unknown'}</p>
        {siemSummary?.sources.length ? siemSummary.sources.map((source) => (
          <p key={source.source}>{eventSources[source.source] || 'Other source'}: last received {formatDateTime(source.last_received_at || undefined)}
            {source.last_scanned_at ? ` · Last scan ${formatDateTime(source.last_scanned_at)}` : ''}
            {source.scan_overdue ? ' · Scan overdue' : ''}
            {source.last_gap_at ? ` · Possible gap ${formatDateTime(source.last_gap_at)}: ${source.gap_note}` : ''}</p>
        )) : <p>No source health reported yet.</p>}
      </section>

      <div style={{ marginBottom: '24px' }}>
        <RecordsTableCard
          title="Authentication & Access Activity"
          description="Recent audit-log details for failed logins, account locks, blocked sign-ins, and unlocks."
          meta={`${lockoutData?.count ?? 0} audit event${lockoutData?.count === 1 ? '' : 's'}`}
          footer={<div className="table-actions">
            <button type="button" disabled={!lockoutData?.previous} onClick={() => setLockoutPage((page) => Math.max(1, page - 1))}>Previous audit page</button>
            <span>Audit page {lockoutPage} · {lockoutData?.count ?? 0} events</span>
            <button type="button" disabled={!lockoutData?.next} onClick={() => setLockoutPage((page) => page + 1)}>Next audit page</button>
          </div>}
        >
          <table className="records-table">
            <thead>
              <tr>
                <th>Event</th><th>Severity</th><th>Account</th><th>Reason</th><th>Failed Attempts</th>
                <th>Source IP</th><th>Locked Time</th><th>Expected Unlock</th><th>Result / Status</th><th>Administrator Unlock</th>
              </tr>
            </thead>
            <tbody>
              {recentLockoutLogs.length === 0 ? (
                <tr><td colSpan={10}><EmptyTableState title="No recent authentication activity" description="Failed sign-ins, locks, and unlocks will appear here." /></td></tr>
              ) : recentLockoutLogs.map((log) => {
                const details = safeDetails(log);
                const presentation = getLockoutPresentation(log.event)!;
                return (
                  <tr key={log.id}>
                    <td>{presentation.label}</td>
                    <td><StatusBadge status={presentation.severity} label={presentation.severity} tone={presentation.severityTone} /></td>
                    <td>{accountLabel(log, details)}</td>
                    <td>{reasonLabel(details, presentation)}</td>
                    <td>{value(details.failed_attempt_count)}</td>
                    <td>{value(log.source_ip)}</td>
                    <td>{formatDateTime(details.locked_at || (normalizedEvent(log.event) === 'ACCOUNT_LOCKED' ? log.timestamp : undefined))}</td>
                    <td>{formatDateTime(details.lockout_until || undefined)}</td>
                    <td><StatusBadge status={presentation.status} label={presentation.status} tone={presentation.statusTone} /></td>
                    <td>{administratorUnlockLabel(log.event, details)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </RecordsTableCard>
      </div>

      <div className="dashboard-grid">
        <section className="panel" style={{ alignSelf: 'start' }}>
          <div className="section-heading"><h2>Session Timeouts</h2><span>Latest {Math.min(sessionEvents.length, 8)} audit rows</span></div>
          {sessionEvents.length === 0 ? <div className="workspace-empty"><strong>No session timeouts</strong><p>No session timeouts are recorded in the audit log.</p></div> : (
            <div className="health-list">{sessionEvents.slice(0, 8).map((log) => <div className="health-row" key={log.id}><span>Session timed out</span><strong>{value(log.username || log.user)}</strong><small>{formatDateTime(log.timestamp)}</small></div>)}</div>
          )}
        </section>

        <section className="panel">
          <div className="section-heading"><h2>Backup History</h2><span>{backupRuns.length}</span></div>
          {backupRuns.length === 0 ? <div className="workspace-empty"><strong>No backup runs</strong><p>Backup history will appear after backup jobs run.</p></div> : (
            <div className="table-wrap"><table><thead><tr><th>Started</th><th>Type</th><th>Trigger</th><th>Status</th><th>Encrypted</th></tr></thead><tbody>
              {backupRuns.slice(0, 8).map((backup) => <tr key={backup.id}><td>{formatDateTime(backup.started_at)}</td><td>{value(backup.backup_type)}</td><td>{value(backup.trigger_source)}</td><td>{value(backup.status)}</td><td>{backup.encrypted ? 'Yes' : 'No'}</td></tr>)}
            </tbody></table></div>
          )}
        </section>
      </div>
    </div>
  );
};

export default SecurityCenter;
